import clsx from 'clsx';
import dayjs from 'dayjs';
import { ChangeEventHandler, useRef, useState } from 'react';
import { dateMatchModifiers, DayPicker, DayPickerProps } from 'react-day-picker';
import 'react-day-picker/style.css';

interface Props {
  onDone: (date: Date | null) => void;
  value: null | Date;
  disabledDate?: Date;
}

const generateTodayMidnight = () => dayjs().millisecond(0).second(0).minute(0).hour(0).toDate();
const todayMidnight = generateTodayMidnight();
const currentYear = todayMidnight.getFullYear();
const availableYears = Array.from(
  {
    length: 5,
  },
  (_, i) => currentYear + i,
);

const defaultDisabledDate = dayjs().millisecond(0).second(0).minute(0).hour(0).add(10, 'day').toDate();

const EventDatePicker = (props: Props) => {
  const { onDone, value, disabledDate } = props;
  const resolvedDisabledDate = disabledDate || defaultDisabledDate;
  const dayPickerDisabledDate: DayPickerProps['disabled'] = {
    before: resolvedDisabledDate,
  };
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (!dayPickerDisabledDate) {
      return value || todayMidnight;
    }

    if (value && !dateMatchModifiers(value, dayPickerDisabledDate)) {
      return value;
    }

    if (todayMidnight && !dateMatchModifiers(todayMidnight, dayPickerDisabledDate)) {
      return todayMidnight;
    }

    return null;
  });
  const [panelDate, setPanelDate] = useState(() => {
    if (value) return value;

    if (dayPickerDisabledDate.before.getMonth() !== new Date().getMonth()) {
      return dayPickerDisabledDate.before;
    }

    return todayMidnight;
  });
  const prevPropsRef = useRef({
    value,
  });

  if (prevPropsRef.current.value !== value) {
    prevPropsRef.current.value = value;
    const nextValue =
      value ||
      (dayPickerDisabledDate.before.getMonth() !== new Date().getMonth()
        ? dayPickerDisabledDate.before
        : generateTodayMidnight());

    setSelectedDate(nextValue);
    setPanelDate(nextValue);
  }

  const handleMonthChange: ChangeEventHandler<HTMLSelectElement> = e => {
    const month = e.target.value;

    setPanelDate(dayjs(panelDate).month(parseInt(month)).millisecond(0).second(0).minute(0).hour(0).toDate());
  };

  const handleYearChange: ChangeEventHandler<HTMLSelectElement> = e => {
    const year = e.target.value;

    setPanelDate(dayjs(panelDate).year(parseInt(year)).millisecond(0).second(0).minute(0).hour(0).toDate());
  };

  const handleSwitchMonth = (actionType: 'next' | 'prev') => {
    if (actionType === 'prev') {
      const nextDate = dayjs(panelDate).subtract(1, 'month').toDate();

      if (!availableYears.includes(nextDate.getFullYear())) {
        return;
      }

      setPanelDate(nextDate);

      return;
    }

    if (actionType === 'next') {
      const nextDate = dayjs(panelDate).add(1, 'month').toDate();

      if (!availableYears.includes(nextDate.getFullYear())) {
        return;
      }

      setPanelDate(nextDate);
    }
  };

  const handleGoToday = () => {
    const today = generateTodayMidnight();

    setSelectedDate(today);
    setPanelDate(today);
  };

  return (
    <div className='flex'>
      <DayPicker
        mode='single'
        month={panelDate}
        required
        selected={selectedDate || undefined}
        onSelect={setSelectedDate}
        classNames={{
          day_button: 'w-full px-1.5 h-full text-sm leading-5',
          day: 'w-10 h-8 p-0',
          selected: 'bg-80583F text-white',
          today: 'text-80583F',
          weekday: clsx('text-sm leading-5 uppercase py-[5px] font-normal '),
        }}
        disabled={dayPickerDisabledDate}
        components={{
          MonthCaption() {
            return (
              <div className='mb-[11px] flex items-center justify-between'>
                <button
                  className='pl-0.5 pr-[5px]'
                  type='button'
                  onClick={() => {
                    handleSwitchMonth('prev');
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='6'
                    height='10'
                    fill='none'
                    viewBox='0 0 6 10'
                  >
                    <path
                      fill='#80583F'
                      d='M4.874.659.238 4.498a.66.66 0 0 0-.238.5.64.64 0 0 0 .238.5l4.636 3.84c.442.367 1.126.063 1.126-.5v-7.68c0-.563-.684-.867-1.126-.5'
                    />
                    <path
                      fill='#000'
                      fillOpacity='0.2'
                      d='M4.874.659.238 4.498a.66.66 0 0 0-.238.5.64.64 0 0 0 .238.5l4.636 3.84c.442.367 1.126.063 1.126-.5v-7.68c0-.563-.684-.867-1.126-.5'
                    />
                  </svg>
                </button>

                <div className='flex items-center gap-x-2.5'>
                  <div className='relative border-1 border-solid border-[#ABA6A0] bg-transparent'>
                    <select
                      className={clsx('h-[30px] w-[111px] appearance-none', 'px-2.5', 'bg-transparent')}
                      onChange={handleMonthChange}
                      value={panelDate.getMonth().toString()}
                    >
                      {monthNames.map((x, i) => {
                        return (
                          <option
                            key={x}
                            value={i}
                          >
                            {x}
                          </option>
                        );
                      })}
                    </select>
                    {selectArrow}
                  </div>

                  <div className='relative border-1 border-solid border-[#ABA6A0] bg-transparent'>
                    <select
                      className={clsx('h-[30px] w-[87px] appearance-none', 'px-2.5', 'bg-transparent')}
                      value={panelDate.getFullYear()}
                      onChange={handleYearChange}
                    >
                      {availableYears.map((x, i) => {
                        return (
                          <option
                            key={i}
                            value={x}
                          >
                            {x}
                          </option>
                        );
                      })}
                    </select>
                    {selectArrow}
                  </div>
                </div>

                <button
                  className='pl-0.5 pr-[5px]'
                  type='button'
                  onClick={() => {
                    handleSwitchMonth('next');
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='6'
                    height='10'
                    fill='none'
                    viewBox='0 0 6 10'
                    className='rotate-180'
                  >
                    <path
                      fill='#80583F'
                      d='M4.874.659.238 4.498a.66.66 0 0 0-.238.5.64.64 0 0 0 .238.5l4.636 3.84c.442.367 1.126.063 1.126-.5v-7.68c0-.563-.684-.867-1.126-.5'
                    />
                    <path
                      fill='#000'
                      fillOpacity='0.2'
                      d='M4.874.659.238 4.498a.66.66 0 0 0-.238.5.64.64 0 0 0 .238.5l4.636 3.84c.442.367 1.126.063 1.126-.5v-7.68c0-.563-.684-.867-1.126-.5'
                    />
                  </svg>
                </button>
              </div>
            );
          },
          Root(props) {
            return (
              <div
                {...props}
                className={clsx(props.className, 'bg-[#F8F3EF] px-6 pb-[33px] pt-[22px]')}
              />
            );
          },
        }}
        hideNavigation
        footer={
          <div className='mt-[22px] flex items-center gap-x-[14px]'>
            <button
              type='button'
              className={clsx(
                'flex h-12 flex-1 items-center justify-center text-sm leading-none text-80583F ',
                'rounded-[3px]',
                'border-1 border-solid border-80583F',
              )}
              onClick={handleGoToday}
            >
              GO TODAY
            </button>
            <button
              type='button'
              className={clsx(
                'flex h-12 flex-1 items-center justify-center text-sm leading-none ',
                'rounded-[3px]',
                'bg-80583F text-white',
              )}
              onClick={() => {
                if (selectedDate && selectedDate < dayPickerDisabledDate.before) return;

                onDone?.(selectedDate || null);
              }}
            >
              DONE
            </button>
          </div>
        }
      />
    </div>
  );
};

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const selectArrow = (
  <span className='pointer-events-none  absolute right-[15px] top-1/2 flex h-[15px] w-[15px] -translate-y-1/2 items-center justify-center'>
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='8'
      fill='none'
      viewBox='0 0 12 8'
    >
      <path
        stroke='#80583F'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M1.781 1.889 6 6.107l4.219-4.218'
      />
      <path
        stroke='#000'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeOpacity='0.2'
        strokeWidth='2'
        d='M1.781 1.889 6 6.107l4.219-4.218'
      />
    </svg>
  </span>
);

export default EventDatePicker;
